import { Component, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import {
  CompetitorPricing,
  SharedService,
} from 'src/app/shared/services/shared.service';
import {
  GridOptions,
  GridApi,
  ColDef,
  RowClickedEvent,
  RowSelectionOptions,
} from 'ag-grid-community';
import { PricingColumnDef } from './columnDefs';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
import { ModalID } from 'src/app/shared/models/constants';

@Component({
    selector: 'app-competitor-pricing',
    templateUrl: './competitor-pricing.component.html',
    styleUrls: ['./competitor-pricing.component.scss'],
    standalone: true,
    imports: [NgClass, AgGridAngular]
})
export class CompetitorPricingComponent implements OnInit {
  pricing_data: CompetitorPricing[] = [];
  distributor_price: number;
  average_price: number = 0; 
  average_price_margin: any = 0; 
  cheapest_price: number = 0; 
  cheapest_price_margin: any = 0; 
  // may want to include distributor shipping cost etc
  // might also want to include FFL's transfer cost

  // AG-Grid properties
  gridApi!: GridApi; // Non-null assertion since set in onGridReady
  
  columnDefs: ColDef[] = PricingColumnDef;
  defaultColDef: any; // Define if needed
  detailCellRendererParams: any;
  overlayLoadingTemplate: string | undefined;
  overlayNoRowsTemplate: string | undefined;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    rowClassRules: {
      'lowest-price-row': (params) => {
        if (!params.data || params.data.price == null) return false;
        let allPrices: number[] = [];
        params.api.forEachNode(node => {
          if (node.data && node.data.price != null) {
            allPrices.push(node.data.price);
          }
        });
        const minPrice = Math.min(...allPrices);
        return params.data.price === minPrice;
      },
    },
  };
  components: any;
  theme = agGridDarkTheme;
  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };

  constructor(public sharedService: SharedService) {}

  ngOnInit(): void {
    this.updatePricingCalculations();
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    
    this.gridApi.setGridOption('rowData', this.pricing_data); // Ensure grid reflects data
  }

  closeModalClick() {
    this.sharedService.closeModal(ModalID.competitor_pricing);
  }

  onRowClicked(event: RowClickedEvent) { // not being used for anything at this point
    console.log('Row Data:', event.data);
  }

  private updatePricingCalculations(): void {
    this.average_price = this.getAveragePrice(this.pricing_data);
    this.cheapest_price = this.getCheapestPrice(this.pricing_data);
    this.average_price_margin = this.calculateAveragePriceMargin();
    this.cheapest_price_margin = this.calculateCheapestPriceMargin();
  }

  getAveragePrice(pricingData: CompetitorPricing[]): number {
    if (!Array.isArray(pricingData) || pricingData.length === 0) {
      return 0;
    }
    const totalPrice = pricingData.reduce(
      (sum, item) => sum + (item.price || 0),
      0
    );
    return Number((totalPrice / pricingData.length).toFixed(2));
  }

  getCheapestPrice(pricingData: CompetitorPricing[]): number {
    if (!Array.isArray(pricingData) || pricingData.length === 0) {
      return 0; // Consistent with average_price
    }
    const validPrices = pricingData
      .filter((item) => typeof item.price === 'number' && !isNaN(item.price))
      .map((item) => item.price);
    return validPrices.length ? Number(Math.min(...validPrices).toFixed(2)) : 0;
  }

  calculateAveragePriceMargin(): number | string {
    let margin: number | string
    if (this.distributor_price) {
      margin = this.average_price - this.distributor_price;
      return margin.toFixed(2);
    } else {
      margin = 'N/A';
      return margin;
    }
  }

  calculateCheapestPriceMargin(): number | string {
    let margin: number | string
    if (this.distributor_price) {
      margin = this.cheapest_price - this.distributor_price;
      return margin.toFixed(2);
    } else {
      margin = 'N/A';
      return margin;
    }
  }

  // Optional: Method to update data dynamically
  updatePricingData(newData: CompetitorPricing[]) {
    this.pricing_data = newData;
    this.updatePricingCalculations();
    this.gridApi.setGridOption('rowData', this.pricing_data); // Refresh grid
  }
}
